import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal, WritableSignal, AfterViewInit } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { faLock, faMinus, faPlus, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { provideTranslationScope, TranslationPipeModule, TranslationStoreService } from "@ogs-gmbh/ngx-translate";
import { distinctUntilChanged, tap } from 'rxjs';
import { LabeledRadioGroupComponent } from '@shared/components/input/radiogroup/labeled-radiogroup.component';
import { MatDivider } from '@angular/material/divider';
import { CommonLookupService } from '@core/services/common-lookup/common-lookup.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { STATUS_TYPES } from '@shared/types/status.types';
import { ComponentToastService } from '@shared/services/component-toast/component-toast.service';
import { ProjectSpecificationsDataService } from './services/project-specifications.service';
import { FormFooterComponent } from '@shared/components/form-footer/form-footer.component';
import { FormStateService } from '@core/services/form-state.service';
import { FORM_FIELD_ERRORS } from '@shared/consts/form-field-errors.const';
import { provideNgxMask, NgxMaskDirective } from 'ngx-mask';
import { BaseSelectComponent } from '@shared/components/base-select/base-select.component';
import { MatAutocompleteOrigin } from '@angular/material/autocomplete';
import { BaseSelectGetItems } from '@shared/components/base-select/types/base-select-get-items.type';
import { ToFormDefaultAtomic } from '@ogs-gmbh/ngx-utils';
import { ProjectSpecifications, ProjectAllocationType, ProjectCategory, ProjectClientType, ProjectClientTypeCategory, ProjectClientTypeModel, ProjectCurrency, ProjectType, ProjectScope, ProjectUsage } from './types/project-specifications.type';
import { PageableFetchOptions } from '@shared/types/api/pageable-options.type';
import { FormOf } from '@shared/types/form-of.type';
import { isEqual } from 'es-toolkit';
import { parseToProjectSpecifications, parseToProjectSpecificationsForm, ProjectSpecificationsForm } from './types/project-specifications-form.type';
import { DirtyCheckDirective } from "@shared/directives/dirty-check/dirty-check.directive";

const TRANSLATION_SCOPE: string = "feature/project-specifications";

export type PreservedFormType = ToFormDefaultAtomic
  | ProjectClientType
  | ProjectAllocationType
  | ProjectCategory
  | ProjectType
  | ProjectScope
  | ProjectUsage
  | ProjectCurrency;
@Component({
  selector: 'app-project-specifications',
  standalone: true,
  templateUrl: './project-specifications.component.html',
  styleUrl: './project-specifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideTranslationScope(TRANSLATION_SCOPE),
    ProjectSpecificationsDataService,
    provideNgxMask({ triggerOnMaskChange: false })
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    IconModule,
    MatButtonModule,
    TranslationPipeModule,
    BaseSelectComponent,
    LabeledRadioGroupComponent,
    MatAutocompleteOrigin,
    MatDivider,
    FormFooterComponent,
    NgxMaskDirective,
    DirtyCheckDirective
  ]
})
export class ProjectSpecificationsComponent implements AfterViewInit {
  private readonly _formBuilder: NonNullableFormBuilder = inject(NonNullableFormBuilder);

  private readonly _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  private readonly _formStateService: FormStateService = inject(FormStateService);

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  private readonly lookup: CommonLookupService = inject(CommonLookupService);

  protected readonly FORM_FIELD_ERRORS: typeof FORM_FIELD_ERRORS = FORM_FIELD_ERRORS;

  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _toastService: ComponentToastService = inject(ComponentToastService);

  private readonly _specificationsDataService: ProjectSpecificationsDataService = inject(ProjectSpecificationsDataService);

  /* eslint-disable @tseslint/unbound-method */
  readonly form: FormOf<ProjectSpecificationsForm, PreservedFormType> = this._formBuilder.group({
    clientType: this._formBuilder.control<ProjectClientType | null>(null),
    clientTypeCategory: this._formBuilder.control<ProjectClientTypeCategory | null>(null),
    clientTypeModel: this._formBuilder.control<ProjectClientTypeModel | null>(null),
    confidential: this._formBuilder.control<boolean | null>(false),
    contractee: this._formBuilder.control<string>("", { validators: [ Validators.required ] }),
    currency: this._formBuilder.control<ProjectCurrency | null>(null, { validators: [ Validators.required ] }),
    grossArea: this._formBuilder.control<string | null>(null),
    grossVolume: this._formBuilder.control<string | null>(null),
    heritageProtected: this._formBuilder.control<boolean | null>(false),
    mainUsage: this._formBuilder.control<ProjectUsage | null>(null),
    allocationType: this._formBuilder.control<ProjectAllocationType | null>(null),
    orderVolume: this._formBuilder.control<string | null>(""),
    category: this._formBuilder.control<ProjectCategory | null>(null, { validators: [ Validators.required ] }),
    projectType: this._formBuilder.control<ProjectType | null>(null, { validators: [ Validators.required ] }),
    scope: this._formBuilder.control<ProjectScope | null>(null),
    sideUsage: this._formBuilder.control<ProjectUsage | null>(null),
    totalOutput: this._formBuilder.control<string | null>("")
  });
  /* eslint-enable @tseslint/unbound-method */

  protected selectedCurrency: WritableSignal<ProjectCurrency | null> = signal(null);

  constructor () {
    this._faIconLibrary.addIcons(faLock, faPlus, faXmark, faMinus);
    this._translationStoreService.getLocale$()
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(isEqual)
      )
      .subscribe(() => {
        this._specificationsDataService.reload();
      });
    this.form.controls.currency.valueChanges
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe((currency: ProjectCurrency | null) => {
        this.selectedCurrency.set(currency);
      });
  }

  protected getProjectAllocationTypes: BaseSelectGetItems<ProjectAllocationType>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectAllocationType>('projects', 'allocation-types', params.searchTerm, params.offset);

  protected getProjectClientTypeCategories: BaseSelectGetItems<ProjectClientTypeCategory>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectClientTypeCategory>('projects', 'client-type-categories', params.searchTerm, params.offset);

  protected getProjectClientTypeModels: BaseSelectGetItems<ProjectClientTypeModel>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectClientTypeModel>('projects', 'client-type-models', params.searchTerm, params.offset);

  protected getProjectClientTypes: BaseSelectGetItems<ProjectClientType>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectClientType>('projects', 'client-types', params.searchTerm, params.offset);

  protected getProjectScopes: BaseSelectGetItems<ProjectScope>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectScope>('projects', 'scopes', params.searchTerm, params.offset);

  protected getProjectTypes: BaseSelectGetItems<ProjectType>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectType>('projects', 'types', params.searchTerm, params.offset);

  protected getProjectCategories: BaseSelectGetItems<ProjectCategory>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectCategory>('projects', 'categories', params.searchTerm, params.offset);

  protected getProjectUsages: BaseSelectGetItems<ProjectUsage>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectUsage>('projects', 'usages', params.searchTerm, params.offset);

  protected getProjectCurrencies: BaseSelectGetItems<ProjectCurrency>
    = (params: PageableFetchOptions) => this.lookup.fetch<ProjectCurrency>('projects', 'currencies', params.searchTerm, params.offset);

  ngAfterViewInit (): void {
    this._specificationsDataService.data$
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((specifications: ProjectSpecifications): void => {
        this.selectedCurrency.set(specifications.currency);
        this.form.patchValue(parseToProjectSpecificationsForm({ ...specifications }));
      });
  }

  protected discardChanges (): void {
    this._formStateService.clear();
    this._specificationsDataService.reload();
  }


  protected saveChanges (): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();

      return;
    }

    const formValue: ProjectSpecifications = parseToProjectSpecifications(this.form.getRawValue());

    this._specificationsDataService.update(formValue)
      .pipe(takeUntilDestroyed(this._destroyRef),
        tap({
          finalize: () => {
            this._formStateService.clear();
          }
        }))
      .subscribe({
        next: () => {
          this._toastService.showToast(STATUS_TYPES.success, {
            defaultText: 'Projektspezifikationen erfolgreich aktualisiert.',
            token: 'status-specifications-updated-successfully',
            scope: TRANSLATION_SCOPE
          });
        },
        error: () => {
          this._toastService.showToast(STATUS_TYPES.error, {
            defaultText: 'Speichern der Projektspezifikationen fehlgeschlagen.',
            token: 'status-specifications-update-error',
            scope: TRANSLATION_SCOPE
          });
        }
      });
  }
}
